import isEqual from 'lodash/isEqual';
import React, { useState, useCallback, useEffect } from 'react';
import { startOfDay, subDays, addDays, endOfDay } from 'date-fns';
// @mui
import { alpha } from '@mui/material/styles';
import { Tab, Tabs, Card, Table, Container, TableBody, TableContainer, Box, FormControl, InputLabel, Select, MenuItem, Stack, Button } from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
// routes
import { paths } from 'src/routes/paths';
// components
import Label from 'src/components/label';
import Scrollbar from 'src/components/scrollbar';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import {
  useTable,
  emptyRows,
  TableEmptyRows,
  TableHeadCustom,
  TablePaginationCustom,
} from 'src/components/table';
//
import { usePaymentActions, usePaymentData } from 'src/context/providers/PaymentContext';
import { apiService } from 'src/utils/apiServices';
//
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import GeorgianPhysicalPaymentTableRow from './GeorgianPhysicalPaymentTableRow';
import UserTableToolbar from '../../custom_components/Table/customer-table-toolbar';
import UserTableFiltersResult from '../../custom_components/Table/customer-table-filters-result';
import { paymentStatus, paymentMethods, payedByOptions, paymentMethodOptions } from './view/georgian-physical-payment-data';
import GeorgianPhysicalPaymentDrawerComponent from './georgian-physical-payment-drawer-component';
import GeorgianPhysicalPaymentTotalsTable from './GeorgianPhysicalPaymentTotalsTable';
import Iconify from '../../components/iconify';
import { PHYSICAL_API } from '../../utils/physical-api';

export default function GeorgianPhysicalPaymentTable({
  STATUS_OPTIONS,
  TABLE_HEAD,
  defaultFilters,
  tableNames,
}) {
  const table = useTable();

  const settings = useSettingsContext();

  const [tableData, setTableData] = useState([]);

  const [filters, setFilters] = useState(defaultFilters);

  const [dateFrom, setDateFrom] = useState(() => startOfDay(subDays(new Date(), 1)));
  const [dateTo, setDateTo] = useState(() => startOfDay(new Date()));

  const [sumUpData, setSumUpData] = useState(null);
  const [sumUpLoading, setSumUpLoading] = useState(false);

  const canReset = !isEqual(defaultFilters, filters);

  const notFound = !tableData.length && canReset;

  const { paidGroups, paidGroupsTotal } = usePaymentData();

  const { getPaidGroups } = usePaymentActions();

  const [aRow, setARow] = useState({});

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const handleDrawerToggle = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const handleFilters = useCallback(
    (name, value) => {
      table.onResetPage();
      setFilters((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    },
    [table]
  );

  const handleResetFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, [defaultFilters]);

  const handleFilterStatus = (event) => {
    handleFilters('payment_status', event.target.value);
  };

  const handleFilterPaymentMethod = (event) => {
    handleFilters('payment_method', event.target.value);
  };

  // const handleFilterPayedBy = (event) => {
  //   handleFilters('payedBy', event.target.value);
  // };

  // const handleFilterPaymentMethods = (event) => {
  //   handleFilters('paymentMethods', event.target.value);
  // };

  const handleViewRow = useCallback(
    (event, row) => {
      setARow(row);
      handleDrawerToggle();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );


  // const handleDeleteRow = useCallback(
  //   (event, row) => {
  //     setARow(row);
  //     handleDrawerToggle();
  //   },
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  //   []
  // );

  const applyFilter = {
    payment_status: (inputData, filter) =>
      inputData.filter((data) => {
        if (filter.length === 0 || filter.includes('all')) return true;
        return filter.includes(data.payed);
      }),
    payment_method: (inputData, filter) =>
      inputData.filter((data) => {
        if (filter.length === 0 || filter.includes('all')) return true;
        return filter.includes(data.payed_by);
      }),
    payedBy: (inputData, filter) =>
      inputData.filter((data) => {
        if (!filter || filter.length === 0 || filter.includes('all')) return true;
        return filter.includes(data.payed_by);
      }),
    paymentMethods: (inputData, filter) =>
      inputData.filter((data) => {
        if (!filter || filter.length === 0 || filter.includes('all')) return true;
        return filter.includes(data.paymentMethod);
      }),
  };

  const dataFiltered = applyFilter.payment_status(
    applyFilter.payment_method(
      applyFilter.payedBy(
        applyFilter.paymentMethods(paidGroups, filters.paymentMethods),
        filters.payedBy
      ),
      filters.payment_method
    ),
    filters.payment_status
  );


  useEffect(() => {
    getPaidGroups({ page: table.page + 1, take: table.rowsPerPage, ...filters });
  }, [getPaidGroups, table.page, table.rowsPerPage, filters]);

  useEffect(() => {
    setTableData(paidGroups);
  }, [paidGroups]);

  useEffect(() => {
    const fetchSumUp = async () => {
      if (!dateFrom || !dateTo) return;
      setSumUpLoading(true);
      try {
        const response = await apiService.get(`${PHYSICAL_API.payments}/sum-up`, {
          params: {
            from: startOfDay(dateFrom).toISOString(),
            to: endOfDay(dateTo).toISOString(),
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        setSumUpData(response.data ?? null);
      } catch (error) {
        console.error('Error fetching sum-up:', error);
        setSumUpData(null);
      } finally {
        setSumUpLoading(false);
      }
    };
    fetchSumUp();
  }, [dateFrom, dateTo]);

  const handleDownloadPhysicalOrderSumUpExcel = async () => {
    try {
      const { data, headers } = await apiService.get(`${PHYSICAL_API.payments}/sum-up/export`, {
        params: {
          from: startOfDay(dateFrom).toISOString(),
          to: endOfDay(dateTo).toISOString(),
        },
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      let fileName = 'physical-orders-sum-up.csv';
      const disposition = headers['content-disposition'];

      if (disposition) {
        const match = disposition.match(/filename="?(.+?)"?$/);
        if (match) fileName = match[1] ?? fileName;
      }

      const url = window.URL.createObjectURL(data);
      const a = document.createElement('a');

      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('File downloaded successfully');
    } catch (error) {
      console.error('Error downloading warehouse Excel:', error);
      toast.error('Failed to download file');
    }
  };

  const handleDownloadPhysicalOrderSumUpDetailsExcel = async () => {
    try {
      const { data, headers } = await apiService.get(`${PHYSICAL_API.payments}/sum-up/export/details`, {
        params: {
          from: startOfDay(dateFrom).toISOString(),
          to: endOfDay(dateTo).toISOString(),
        },
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      let fileName = 'physical-orders-sum-up-details.csv';
      const disposition = headers['content-disposition'];

      if (disposition) {
        const match = disposition.match(/filename="?(.+?)"?$/);
        if (match) fileName = match[1] ?? fileName;
      }

      const url = window.URL.createObjectURL(data);
      const a = document.createElement('a');

      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('File downloaded successfully');
    } catch (error) {
      console.error('Error downloading warehouse Excel:', error);
      toast.error('Failed to download file');
    }
  };

  return (
    <>
      <Container maxWidth={settings.themeStretch ? false : 'xl'}>
        <CustomBreadcrumbs
          heading="Physical Orders Payments Sum Up"
          sx={{ mb: { xs: 3, md: 5 } }}
        />

        <Card
          sx={{ mb: { xs: 3, md: 5 } }}
        >
          <Box sx={{ px: 2.5, py: 2 }}>
            <Stack direction="row" spacing={2}>
              <DesktopDatePicker
                label="From"
                views={['year', 'month', 'day']}
                format="MM/dd/yyyy"
                value={dateFrom}
                onChange={(newValue) => {
                  setDateFrom(newValue);
                  if (newValue && dateTo && newValue >= dateTo) setDateTo(startOfDay(addDays(newValue, 0)));
                }}
                maxDateTime={dateTo ? startOfDay(subDays(dateTo, 0)) : undefined}
              />

              <DesktopDatePicker
                label="To"
                views={['year', 'month', 'day']}
                format="MM/dd/yyyy"
                value={dateTo}
                onChange={(newValue) => {
                  setDateTo(newValue);
                  if (newValue && dateFrom && newValue <= dateFrom) setDateFrom(startOfDay(subDays(newValue, 0)));
                }}
                minDateTime={dateFrom ? startOfDay(addDays(dateFrom, 0)) : undefined}
              />

              <Button
                variant="outlined"
                size="medium"
                startIcon={<Iconify icon="material-symbols:download" />}
                disabled={sumUpData?.length === 0}
                onClick={handleDownloadPhysicalOrderSumUpExcel}
                sx={{ ml: 1 }}
              >
                Sum Up
              </Button>
              <Button
                variant="outlined"
                size="medium"
                startIcon={<Iconify icon="material-symbols:download" />}
                disabled={sumUpData?.length === 0}
                onClick={handleDownloadPhysicalOrderSumUpDetailsExcel}
                sx={{ ml: 1 }}
              >
                Details
              </Button>
            </Stack>
          </Box>
          <GeorgianPhysicalPaymentTotalsTable data={sumUpData} loading={sumUpLoading} />
        </Card>

        <CustomBreadcrumbs
          heading="Georgian Physical Order Payments"
          links={[
            { name: 'Dashboard', href: paths.dashboard.root },
            { name: 'Payments', href: paths.dashboard.payment.root },
            { name: 'Georgian Physical Orders' },
          ]}
          sx={{ mb: { xs: 3, md: 5 } }}
        />

        <Card>
          <Tabs
            value={filters.payment_status}
            onChange={(event, newValue) => handleFilters('payment_status', newValue)}
            sx={{
              px: 2.5,
              boxShadow: (theme) => `inset 0 -2px 0 0 ${alpha(theme.palette.grey[500], 0.08)}`,
            }}
          >
            {STATUS_OPTIONS.map((tab) => (
              <Tab
                key={tab.value}
                iconPosition="end"
                value={tab.value}
                label={tab.label}
                icon={
                  <Label
                    variant={
                      ((tab.value === 'all' || tab.value === filters.payment_status) && 'filled') ||
                      'soft'
                    }
                    color={
                      (tab.value === 'all' && 'default') ||
                      (tab.value === true && 'success') ||
                      (tab.value === false && 'warning') ||
                      'default'
                    }
                  >
                    {tab.value === 'all' && tableData.length}
                    {tab.value === true &&
                      tableData.filter((payment) => payment.payed === true).length}
                    {tab.value === false &&
                      tableData.filter((payment) => payment.payed === false).length}
                  </Label>
                }
              />
            ))}
          </Tabs>

          <UserTableToolbar
            filters={filters}
            onFilters={handleFilters}
            //
            canReset={canReset}
            onResetFilters={handleResetFilters}
            //
            filterStatus={filters.payment_status}
            onFilterStatus={handleFilterStatus}
            statusOptions={paymentStatus}
            //
            filterPaymentMethod={filters.payment_method}
            onFilterPaymentMethod={handleFilterPaymentMethod}
            paymentMethodOptions={paymentMethods}
          />

          <Box sx={{ px: 2.5, pb: 2 }}>
            <Stack direction="row" spacing={2}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Paid By</InputLabel>
                <Select
                  value={filters.payedBy || 'all'}
                  label="Paid By"
                  onChange={(e) => handleFilters('payedBy', e.target.value)}
                >
                  {payedByOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={filters.paymentMethods || 'all'}
                  label="Payment Method"
                  onChange={(e) => handleFilters('paymentMethods', e.target.value)}
                >
                  {paymentMethodOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Box>

          {canReset && (
            <UserTableFiltersResult
              filters={filters}
              onFilters={handleFilters}
              //
              onResetFilters={handleResetFilters}
              //
              results={dataFiltered.length}
              sx={{ p: 2.5, pt: 0 }}
            />
          )}

          <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
            <Scrollbar>
              <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 960 }}>
                <TableHeadCustom
                  order={table.order}
                  orderBy={table.orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={tableData.length}
                  numSelected={table.selected.length}
                  onSort={table.onSort}
                  onSelectAllRows={(checked) =>
                    table.onSelectAllRows(
                      checked,
                      tableData.map((row) => row.id)
                    )
                  }
                />

                <TableBody>
                  {dataFiltered.map((row) => (
                      <GeorgianPhysicalPaymentTableRow
                        key={row.id}
                        row={row}
                        selected={table.selected.includes(row.id)}
                        onSelectRow={() => table.onSelectRow(row.id)}
                        onViewRow={(event) => handleViewRow(event, row)}
                      />
                    ))}

                  <TableEmptyRows
                    height={table.denseHeight}
                    emptyRows={emptyRows(table.page, table.rowsPerPage, paidGroupsTotal)}
                  />

                  {notFound && <TableEmptyRows height={table.denseHeight} emptyRows={1} />}
                </TableBody>
              </Table>
            </Scrollbar>
          </TableContainer>

          <TablePaginationCustom
            count={paidGroupsTotal}
            page={table.page}
            rowsPerPage={table.rowsPerPage}
            onPageChange={table.onChangePage}
            onRowsPerPageChange={table.onChangeRowsPerPage}
            //
            dense={table.dense}
            onChangeDense={table.onChangeDense}
          />
        </Card>
      </Container>

      <GeorgianPhysicalPaymentDrawerComponent
        isDrawerOpen={isDrawerOpen}
        handleDrawerToggle={handleDrawerToggle}
        aRow={aRow}
        setARow={setARow}
      />
    </>
  );
}

GeorgianPhysicalPaymentTable.propTypes = {
  STATUS_OPTIONS: PropTypes.array,
  TABLE_HEAD: PropTypes.array,
  defaultFilters: PropTypes.object,
  tableNames: PropTypes.array,
};
